import { ARTIFACT_DROP } from '../inventory/artifacts';
import { CARD_IDS } from '../inventory/cards';
import { dropIdFor, type KillIdentity, lootUniformBelow } from './loot';

/**
 * **A terceira loteria de uma morte confirmada: a CARTA** (gate V28H.E).
 *
 * ## Independente, e a independência é estrutural
 *
 * Cada morte elegível faz três sorteios: artefacto, equipamento e carta. Os três
 * partilham o segredo da sessão e mais nada - cada um assina uma MENSAGEM
 * diferente, e um HMAC sobre mensagens diferentes produz digests independentes.
 *
 * É por isso que acrescentar esta loteria não desloca um único resultado
 * antigo: os digests de `${dropId}` e de `${dropId}:equipment` são exactamente
 * os que sempre foram, porque nada nesta linha os toca. Uma morte pode produzir
 * as três coisas, uma, ou nenhuma, e as probabilidades não se dividem entre si.
 *
 * A alternativa - lanes livres do MESMO digest - teria sido mais barata em
 * bytes e errada em espírito: quatro lanes por digest são um orçamento fixo, e
 * a rejeição de uma delas passaria a poder mudar o que outro sistema lê.
 *
 * ## A chance é a do artefacto, LIDA e não copiada
 *
 * O contrato aprovado diz "exactamente a mesma chance de um artefacto", e a
 * única maneira honesta de escrever isso é ler `ARTIFACT_DROP`. Um `6000` e um
 * `1000` copiados para aqui seriam dois números a envelhecer em separado, e o
 * dia em que a chance de artefacto mudasse seria o dia em que a promessa de
 * igualdade passaria a ser falsa sem que nada o assinalasse.
 *
 * ## O que ela nunca é
 *
 * Nunca `Math.random`. Nunca o gerador da SIMULAÇÃO, cuja semente é pública e
 * que um F5 voltaria a mintar. Nunca uma lane que o artefacto ou o equipamento
 * já leiam. E nunca uma decisão do cliente: não há campo, cabeçalho ou
 * parâmetro em que uma carta possa ser pedida.
 */

/**
 * A mensagem que decide SE uma carta caiu.
 *
 * `:card`, ao lado de `:equipment`: o mesmo formato, o mesmo separador, e um
 * sufixo que nenhuma outra loteria usa.
 */
export function cardDropMessage(kill: KillIdentity): string {
  return `${dropIdFor(kill)}:card`;
}

/** A mensagem que decide QUAL carta, depois de uma ter caído. */
export function cardSpeciesMessage(kill: KillIdentity): string {
  return `${dropIdFor(kill)}:card:species`;
}

/**
 * As dezasseis, na ordem canónica do catálogo.
 *
 * Derivada de `CARD_IDS` e não escrita à mão: o índice sorteado é um índice
 * NESTA lista, e a identidade que fica persistida é o id que ele nomeia - nunca
 * o número. Um catálogo reordenado muda o que um digest antigo escolheria, o
 * que é uma mudança real e é apanhada pelos vectores de resposta conhecida.
 */
export const CARD_DROP_TABLE: readonly string[] = Object.freeze([...CARD_IDS]);

/** O denominador desta morte: exactamente o do artefacto. */
export function cardDropDenominator(wasBoss: boolean): number {
  return wasBoss ? ARTIFACT_DROP.bossKillDenominator : ARTIFACT_DROP.normalKillDenominator;
}

/** O que o sorteio de uma morte decidiu. */
export interface CardDropRoll {
  readonly dropId: string;
  readonly dropped: boolean;
}

/**
 * Se esta morte deixou cair uma carta.
 *
 * Lane 0 (e 1, se a primeira for rejeitada), sobre o digest de `:card`. O
 * digest é INTEIRO deste sorteio, portanto começar na lane 0 não colide com
 * nada: `decideDrop` começa na lane 0 de OUTRO digest.
 *
 * Amostragem por rejeição pelo helper canónico, porque o ingénuo `n % 6000` é
 * enviesado: 2^64 não é múltiplo de 6000, e os resíduos mais baixos sairiam uma
 * vez a mais.
 */
export function decideCardDrop(dropId: string, digest: Uint8Array, wasBoss: boolean): CardDropRoll {
  const chance = lootUniformBelow(digest, BigInt(cardDropDenominator(wasBoss)), 0);
  return { dropId, dropped: chance === 0n };
}

/**
 * Qual das dezasseis, uniformemente.
 *
 * Um digest PRÓPRIO - o de `:card:species` - e por isso lane 0. A raridade
 * decide o que uma carta faz, nunca a probabilidade de ela cair: uma Leviatã
 * Aéreo prismática é exactamente tão provável como um Arranque comum, pela
 * mesma regra que o artefacto já segue.
 *
 * `null` quando todas as lanes caem na cauda rejeitada, o que para dezasseis é
 * astronomicamente improvável: quem chama trata a morte como sem queda em vez
 * de adivinhar, para que uma resposta em falta nunca invente um item.
 */
export function pickCardDefinition(digest: Uint8Array): string | null {
  const pick = lootUniformBelow(digest, BigInt(CARD_DROP_TABLE.length), 0);
  if (pick === null) {
    return null;
  }
  return CARD_DROP_TABLE[Number(pick)] ?? null;
}
